/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Xaero Zero - xaerozero@icloud.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {SearchableItem} from './interface/searchable.item';
import {SearchableSelectionType} from './interface/searchable.selection';
import {Board} from './board';

export class User implements SearchableItem {
  ID: string;
  EMAIL: string;
  FIRST_NAME: string;
  LAST_NAME: string;
  STATE: UserState;
  WORKING_DIRECTORY: string;
  USER_ROLES: Role[];
  NOTIFICATIONS: Notification[];
  SIGNATURE: string;
  USER_POSITIONS: Position[];
  BOARDS: Board[];
  NAME_PREF: UserNamePref;
  THEME_COLOR: string;
  THEME_NIGHT: boolean;
  TFA: boolean;
  AVATAR_COLOR: string;
  AVATAR_URL: string;

  // Helpers
  active = false;
  selected = false;
  allowed = false;

  static parse(json: any): User {
    if (json) {
      const u = new User();

      u.ID = json.ID;
      u.EMAIL = json.EMAIL;
      u.FIRST_NAME = json.FIRST_NAME;
      u.LAST_NAME = json.LAST_NAME;
      u.STATE = UserState[<string>json.STATE];
      u.USER_ROLES = Role.parseArray(json.USER_ROLES);
      u.NOTIFICATIONS = Notification.parseArray(json.NOTIFICATIONS);
      u.WORKING_DIRECTORY = json.WORKING_DIRECTORY;
      u.SIGNATURE = json.SIGNATURE;
      u.USER_POSITIONS = Position.parseArray(json.USER_POSITIONS);
      u.BOARDS = Board.parseArray(json.BOARDS);
      u.NAME_PREF = UserNamePref[<string>json.NAME_PREF];
      u.THEME_COLOR = json.THEME_COLOR;
      u.THEME_NIGHT = json.THEME_NIGHT;
      u.TFA = json.TFA;
      u.AVATAR_COLOR = json.AVATAR_COLOR;
      u.AVATAR_URL = json.AVATAR_URL ? json.AVATAR_URL : null;
      return u;
    } else return null;
  }

  static parseArray(json: any[]): User[] {
    const arr = [];

    if (json)
      for (const j of json)
        if (j)
          arr.push(User.parse(j));

    return arr;
  }

  hasPosition(position: string): boolean {
    if ( this.USER_POSITIONS.find(p => p.CODE === position) ) {
      return true;
    }
    return false;
  }

  getName(): string {
    if (!this.FIRST_NAME || !this.LAST_NAME)
      return this.EMAIL;
    else
      return `${this.FIRST_NAME} ${this.LAST_NAME}`;
  }

  getFullDetails(): string {
    if (!this.FIRST_NAME || !this.LAST_NAME)
      return this.EMAIL;
    else
      return `${this.FIRST_NAME} ${this.LAST_NAME} <${this.EMAIL}>`;
  }

  getType(): SearchableSelectionType {
    return SearchableSelectionType.VALUE;
  }

  getValue(): any {
    return this;
  }

  hasRolePrivilege(code: String): boolean {
    if(this.USER_ROLES.length == 0)
      return false;

    if (this.USER_ROLES.length > 0){
      for(let role of this.USER_ROLES){
        if(role.ROLE_PRIVILEGES.length == 0)
          return false;

        if(role.ROLE_PRIVILEGES.length > 0){
          for(let privilege of role.ROLE_PRIVILEGES){
            if(privilege.CODE == code)
              return true;
          }
        }
      }
    }

    return false;
  }
}

export class Notification {
  ID: string;
  NAME: string;
  GROUP: NotificationGroup;
  SELECTED: boolean;

  static parse(json: any): Notification {
    if (json) {
      const r = new Notification();
      r.ID = json.ID;
      r.NAME = json.NAME;
      r.GROUP = NotificationGroup[<string>json.GROUP];
      r.SELECTED = Boolean(json.SELECTED);

      return r;
    } else return null;
  }

  static parseArray(json: any[]): Notification[] {
    const arr = [];
    if (json)
      for (const j of json)
        if (j)
          arr.push(Notification.parse(j));
    return arr;
  }
}

export class Role implements SearchableItem {
  ID: string;
  NAME: string;
  CODE: string;
  DESC: string;

  ROLE_PRIVILEGES: Privilege[];

  // Helpers
  active = false;
  selected = false;
  allowed = false;

  static parse(json: any): Role {
    if (json) {
      const r = new Role();
      r.ID = json.ID;
      r.NAME = json.NAME;
      r.CODE = json.CODE;
      r.DESC = json.DESC;

      r.ROLE_PRIVILEGES = Privilege.parseArray(json.PRIVILEGES);

      return r;
    } else return null;
  }

  static parseArray(json: any[]): Role[] {
    const arr = [];
    if (json)
      for (const j of json)
        if (j)
          arr.push(Role.parse(j));
    return arr;
  }

  getName(): string {
    return this.NAME;
  }

  getFullDetails(): string {
    return this.NAME;
  }

  getType(): SearchableSelectionType {
    return SearchableSelectionType.VALUE;
  }

  getValue(): any {
    return this;
  }
}

export class Privilege implements SearchableItem {
  ID: string;
  NAME: string;
  CODE: string;
  DESC: string;

  // Helpers
  active = false;
  selected = false;
  allowed = false;

  static parse(json: any): Privilege {
    if (json) {
      const p = new Privilege();

      p.ID = json.ID;
      p.NAME = json.NAME;
      p.CODE = json.CODE;
      p.DESC = json.DESC;

      return p;
    } else return null;
  }

  static parseArray(json: any[]): Privilege[] {
    const arr = [];

    if (json)
      for (const j of json)
        if (j)
          arr.push(Privilege.parse(j));

    return arr;
  }

  getName(): string {
    return this.NAME;
  }

  getFullDetails(): string {
    return this.NAME;
  }

  getType(): SearchableSelectionType {
    return SearchableSelectionType.VALUE;
  }

  getValue(): any {
    return this;
  }
}

export class Position implements SearchableItem {
  ID: string;
  NAME: string;
  CODE: string;
  DESC: string;

  // Helpers
  active = false;
  selected = false;
  allowed = false;

  static parse(json: any): Position {
    if (json) {
      const p = new Position();
      p.ID = json.ID;
      p.NAME = json.NAME;
      p.CODE = json.CODE;
      p.DESC = json.DESC;

      return p;
    } else return null;
  }

  static parseArray(json: any[]): Role[] {
    const arr = [];
    if (json)
      for (const j of json)
        if (j)
          arr.push(Role.parse(j));
    return arr;
  }

  getName(): string {
    return this.NAME;
  }

  getFullDetails(): string {
    return this.NAME;
  }

  getType(): SearchableSelectionType {
    return SearchableSelectionType.VALUE;
  }

  getValue(): any {
    return this;
  }
}

export enum UserState {
  ENABLED = 'ENABLED',
  DISABLED = 'DISABLED',
  INVITED = 'INVITED',
  BLOCKED = 'BLOCKED'
}

export enum NotificationGroup {
  REPORT = 'REPORT',
  WATCH = 'WATCH'
}

export enum UserNamePref {
  CORPORATE = 'CORPORATE',
  BUSINESS = 'BUSINESS',
  INTERNAL = 'INTERNAL'
}
